import React from 'react'
import PropTypes from 'prop-types'
import { Nav, Navbar, NavDropdown, NavItem } from 'react-bootstrap'
import { connect } from 'react-redux'

import expand from '../../assets/images/expand_more_sm.svg'
import { selectCustomer } from '../../containers/App/selectors'
import SearchInput from '../../containers/ESSearch/SearchInput'
import { getVersionIcon, getAbsoluteLink } from '../../helpers/utils'
import Shop from './components/Shop'
import Category from './components/Category'
import Link from './components/Link'
import { NavigationV3, Tab, FlexDirection, Links } from './styled'
import '../../types'
import { NAV_MODULE_ICON } from '../../constants'

const Title = ({ title, noCaret = false }) => {
  return (
    <Tab>
      <div className="animated slideInLeft">{title}</div>
      <div className="animated fadeIn" style={{ animationDelay: '0.8s' }}>
        {!noCaret && <img src={expand} alt="expand" className="blue" />}
      </div>
    </Tab>
  )
}

/**
 * @typedef {Object} NavbarV3Props
 * @property {Header} header
 * @property {function(): void} toggleDeviceModal
 * @property {string} version
 * @property {TopLinks} topLinks
 */

/**
 * NavbarV3
 * The main navigation component tree for within HeaderV3
 * @param {NavbarV3Props}
 * @returns {React.ElementType}
 */
const NavbarV3 = ({ header, toggleSearchModal, topLinks, version }) => {
  if (!Object.keys(header).includes('items')) return null
  const menuItems = header.items.navigation.item
  const { cartIconUrl } = header.items.navigation

  return (
    <NavigationV3
      className="animated slideInDown navbar-shrink"
      id="nav-container"
    >
      <a
        id="brand"
        href={getAbsoluteLink('/', version)}
        className="hidden-xs hidden-sm"
      >
        <img
          src={getAbsoluteLink('content/dam/tracfone/logos/desktop_lg.svg', version)}
          alt="Logo Homepage"
        />
      </a>
      <Navbar className="upper-navbar hidden-sm hidden-xs" id="navbar">
        <NavItem eventKey={1} href={topLinks.url}>
          {
            header.items.languagenavigation.items.find(
              language => !language.active
            ).title
          }
        </NavItem>
        <NavItem eventKey={2} href="#">
          <img
            src={getVersionIcon(NAV_MODULE_ICON.location)}
            alt="location"
            height="24px"
            className="blue"
          />
          <span className="small-text">
            {header.items.locationservices.locationlabel}
          </span>
        </NavItem>
        {header.links.map((link, i) => {
          return (
            <NavItem
              className="text border flex-hidden"
              href={getAbsoluteLink(link.linkUrl, version)}
              alt={link.linkAltText}
              key={`${i}_${link.linkText}`}
            >
              {link.linkText}
            </NavItem>
          )
        })}
      </Navbar>
      <Navbar className="lower-navbar">
        <Navbar.Header>
          <Navbar.Toggle />
          <a id="brand" href={getAbsoluteLink('/', version)} className="hidden-lg hidden-md">
            <img
              src={getAbsoluteLink(
                'content/dam/tracfone/logos/mobile-logo-v1.png',
                version
              )}
              alt="Logo Homepage"
            />
          </a>
          <Nav className="hidden-lg hidden-md icons-container-sm">
            <NavItem
              className="icon"
              href={getAbsoluteLink("createaclogin", version)}
              style={{ padding: '10px' }}
            >
              <span>
                <img
                  src={getVersionIcon(NAV_MODULE_ICON.person)}
                  alt="account"
                  className="blue"
                />
              </span>
            </NavItem>
            <NavItem
              className="icon"
              href={getAbsoluteLink(cartIconUrl, version)}
              style={{ padding: '10px 10px 10px 0px' }}
            >
              <span>
                <img
                  src={getVersionIcon(NAV_MODULE_ICON.shopping)}
                  alt="shopping cart"
                  className="blue"
                />
              </span>
            </NavItem>
          </Nav>
        </Navbar.Header>
        <Navbar.Collapse>
          <Nav>
            <div className="hidden-md hidden-lg search">
              <SearchInput
                toggleSearchModal={toggleSearchModal}
                aria-label="Search Mobile"
              />
            </div>
            {menuItems.map((item, i) => {
              const mItems = item.children
              if (Array.isArray(mItems) && mItems.length > 0) {
                const containsShop = item.title === 'Shop'
                const containsRefill = item.title === 'Refill'
                return (
                  <NavDropdown
                    key={`${i}_${item.title}`}
                    eventKey={3}
                    title={<Title title={item.title} />}
                    noCaret
                  >
                    <FlexDirection className={containsShop ? 'column' : ''}>
                      {!containsShop && (
                        <div
                          className={`title icons ${
                            containsRefill ? 'refill' : ''
                          }`}
                        >
                          <p>{item.flyoutHeader}</p>
                          {item.flyoutButtonLabel && (
                            <Link href={getAbsoluteLink(item.flyoutButtonLink, version)}>Learn more</Link>
                          )}
                        </div>
                      )}
                      <div className="menu">
                        <div
                          style={{ width: '100%' }}
                          className="hidden-lg hidden-md border"
                        >
                          {item.quickLinks.length > 0 && (
                            <Shop
                              data={item.quickLinks}
                              title={item.quickLinksHeading}
                              version={version}
                            />
                          )}
                        </div>
                        <p
                          className={`hidden-md hidden-lg hidden-xl ${
                            containsShop ? 'hidden' : 'category-name'
                          }`}
                        >
                          {item.title}
                        </p>
                        {mItems.map((it, i) => {
                          return (
                            <li
                              key={`${i}_${it.title}`}
                              className={
                                containsShop ? 'shop-category' : 'category'
                              }
                            >
                              <Category
                                data={it}
                                list={it.children}
                                containsShop={containsShop}
                                version={version}
                              />
                            </li>
                          )
                        })}
                      </div>
                      {item.quickLinks.length > 0 && (
                        <Shop
                          data={item.quickLinks}
                          title={item.quickLinksHeading}
                          className="hidden-xs hidden-sm"
                          version={version}
                        />
                      )}
                    </FlexDirection>
                  </NavDropdown>
                )
              }
              return (
                <NavItem
                  className="dropdown menu-item animated slideInLeft"
                  href={getAbsoluteLink(item.url, version)}
                  key={`${item}_${i}`}
                >
                  <Title title={item.title} noCaret />
                </NavItem>
              )
            })}
            <Links>
              {header.items.navigation.navigationLinks.map((item, i) => {
                return (
                  <NavItem
                    key={`${i}_${item.linkText}`}
                    href={getAbsoluteLink(item.linkUrl, version)}
                    rel="noopener noreferrer"
                    className="text-link"
                  >
                    {item.linkText}
                  </NavItem>
                )
              })}
              <NavItem
                className="icon hidden-xs hidden-sm"
                onClick={() => toggleSearchModal()}
              >
                <span>
                  <img
                    src={getVersionIcon(NAV_MODULE_ICON.search, version)}
                    alt="search"
                    className="blue"
                    height="20px"
                  />
                </span>
              </NavItem>
              <NavItem className="icon hidden-xs hidden-sm" href={getAbsoluteLink(cartIconUrl, version)}>
                <span>
                  <img
                    src={getVersionIcon(NAV_MODULE_ICON.shopping)}
                    alt="shopping cart"
                    className="blue"
                  />
                </span>
              </NavItem>
            </Links>
          </Nav>
          <div className="hidden-lg hidden-md switches">
            <Nav>
              <NavItem eventKey={1} href={topLinks.url}>
                <img
                  src={getVersionIcon(NAV_MODULE_ICON.language)}
                  alt="language"
                  height="24px"
                  className="blue"
                />
                {
                  header.items.languagenavigation.items.find(
                    language => !language.active
                  ).title
                }
              </NavItem>
              <NavItem eventKey={2} href="#">
                <img
                  src={getVersionIcon(NAV_MODULE_ICON.location)}
                  alt="location"
                  height="24px"
                  className="blue"
                />
                <span className="small-text">Enter your ZIP</span>
              </NavItem>
              {header.links.map((link, i) => {
                return (
                  <NavItem
                    key={`${i}_${link.linkText}`}
                    className="animated slideInLeft"
                    href={getAbsoluteLink(link.linkUrl, version)}
                    alt={link.linkAltText}
                  >
                    <span className="small-text">{link.linkText}</span>
                  </NavItem>
                )
              })}
            </Nav>
          </div>
        </Navbar.Collapse>
      </Navbar>
    </NavigationV3>
  )
}

NavbarV3.propTypes = {
  version: PropTypes.string.isRequired,
  header: PropTypes.object.isRequired,
  toggleSearchModal: PropTypes.func,
  topLinks: PropTypes.shape({
    url: PropTypes.string.isRequired,
    show: PropTypes.bool,
    text: PropTypes.string
  }).isRequired
}

export default NavbarV3



// WEBPACK FOOTER //
// ./src/components/Navbar/NavbarV3.js